import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LANGS, pathFor, translations } from "@/i18n/translations";
import { siteConfig } from "@/config/site";

const NAV = [
  ["home", "home"], ["about", "about"], ["mechanical", "mechanical"], ["tires", "tires"], ["contact", "contact"],
];

export const Header = () => {
  const { lang, pageKey, t } = useLang();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pageKey, lang]);

  const switchLang = (l) => nav(pathFor(l, pageKey));

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-[#0A0A0C]/90 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 h-24 md:h-28 flex items-center justify-between">
        <Link to={pathFor(lang, "home")} data-testid="logo-link" className="flex items-center group">
          <img src={siteConfig.logo} alt={siteConfig.companyName} className="h-20 md:h-[104px] w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map(([key, page]) => (
            <Link
              key={key}
              to={pathFor(lang, page)}
              data-testid={`nav-${key}`}
              className={`text-sm font-medium tracking-wide transition-colors ${
                pageKey === page ? "text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {t.nav[key]}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <a href={siteConfig.phoneHref} data-testid="header-phone"
            className="hidden xl:flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors">
            <Phone size={15} style={{ color: "var(--accent)" }} /> {siteConfig.phone}
          </a>

          <div className="hidden sm:flex items-center gap-1 text-xs font-semibold">
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => switchLang(l)}
                data-testid={`lang-${l}`}
                className={`px-1.5 py-1 uppercase transition-colors ${
                  lang === l ? "text-white" : "text-zinc-500 hover:text-zinc-300"
                }`}
                aria-label={translations[l].langLabel}
              >
                {l}
              </button>
            ))}
          </div>

          <Link
            to={pathFor(lang, "contact")}
            data-testid="header-book-btn"
            className="hidden md:inline-flex text-sm font-semibold text-white px-5 py-2.5 transition-colors"
            style={{ background: "var(--accent)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--accent)")}
          >
            {t.cta.book}
          </Link>

          <button
            className="lg:hidden text-white p-1"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0A0A0C] border-t border-white/10 px-5 py-6">
          <nav className="flex flex-col gap-1">
            {NAV.map(([key, page]) => (
              <Link
                key={key}
                to={pathFor(lang, page)}
                data-testid={`mobile-nav-${key}`}
                className="py-3 text-lg font-display font-medium text-white border-b border-white/5"
              >
                {t.nav[key]}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 mt-5 text-sm font-semibold">
            {LANGS.map((l) => (
              <button key={l} onClick={() => switchLang(l)} data-testid={`mobile-lang-${l}`}
                className={`px-3 py-1.5 uppercase border ${lang === l ? "border-white text-white" : "border-white/15 text-zinc-500"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
